import i18n from 'i18next'
import * as R from 'ramda'
import { cloneDeep } from 'lodash'
import {
    ObservableMap, action, observable, makeObservable,
} from 'mobx'

import MergeFields from '../../utils/MergeFields'
import { formatV2 } from './Formatter'
import colours from './components/RightPanel/Chart/colours'
import RuleGroup from './components/Modal/Filter/model/RuleGroup'
import { getOptionTitle, getQuestionTitle } from '../../utils/QuestionHelper'
import { getCurrencySymbol, capitalizeFirstLetter } from '../../utils/helpers'
import Rule from './components/Modal/Filter/model/Rule'

class ReportStore {
    @observable benchmarks = new Map()

    benchmarkTags = []

    @observable states = new Map()

    @observable filterIsOpen = false

    @observable isLoading = true

    @observable showAdvancedXTab = false

    reportFilters = []

    reportSurveyDataIds = []

    surveyDataIds = ''

    @observable startDownload = new Date().getTime()

    @observable xTabs = []

    locale = 'en_GB'

    @observable initialised = false

    @observable showDebug = false

    @observable reportDataFetched = new Date().getTime()

    includeTest = false

    @observable locale = 'en_GB'

    @observable stillLoading = false

    includeIncomplete = false

    campaigns = []

    campaignList = []

    years = [{
        id: '',
        year: 0,
    }]

    reportData = {}

    fromDate

    toDate

    parser = new DOMParser()

    questionChartRefs = new Map()

    verbatim = {}

    questionSorting = {}

    @observable areaUnit = 'M2'

    @observable currentCurrency = null

    @observable xtabs = []

    openEnded = new Map()

    @observable surveyStatus = {
        style: '',
        label: '',
        icon: null,
    }

    hasPrepopulatedData = false

    currencySign = {
        USD: '$',
        GBP: '£',
        EUR: '€',
    }

    // new filters
    @observable filters = {
        question: new RuleGroup(RuleGroup.getDefaultLogic('question')),
        campaign: new RuleGroup(RuleGroup.getDefaultLogic('campaign')),
        responseDate: new RuleGroup(RuleGroup.getDefaultLogic('responseDate')),
        openEnded: new RuleGroup(RuleGroup.getDefaultLogic('question')),
    }

    @observable showFilters = false

    @observable showComparison = false

    @observable showBenchmarking = false

    @observable benchmarks2 = new Map()

    @observable yoyComparisons = new Map()

    @observable branchComparisons = new Map()

    @observable showBranches = true

    @observable generatingSummary = false

    availableMetrics = new Map()

    editAnswersURL

    @observable editAnswersModalIsOpen = false

    @observable generatingSentiment = false

    @observable selectedResponseStatuses = new Map(
        [
            ['completed', {
                value: 'completed',
                label: 'Completed',
                noDelete: true,
            }],
        ],
    )

    benchmarkPeriods = [
        {
            period: {
                period: 'ROL',
                duration: 36,
            },
            value: 36,
            label: '36 months',
        },
        {
            period: {
                period: 'ROL',
                duration: 84,
            },
            value: 84,
            label: 'Long term',
        },
    ]

    benchmarkPeriod = this.benchmarkPeriods[0].period

    @observable showExportModal = false

    @observable filteredQuestions = []

    @observable pageQuestionMapNew = new Map()

    @observable isPageQuestionMapNewReady = false

    @observable isReportDataReady = false

    questions = []

    @observable AiSummary = null

    @observable summaryLastUpdated = null

    @observable summaryNumberOfResponses = null

    @observable showAiAssistant = false

    summaryPollCount = 1

    fetchTimeout = null

    exportInit = () => {
        const { appStore: { survey } } = this
        this.questions = R.values(this.reportPages).map((p) => p.questions).flat(2)
            .map((question) => ({
                ...question,
                questionTitle: question.content.shortTitle ? (getQuestionTitle({
                    content: question.content.shortTitle,
                    formContent: R.pathOr(null, ['formContent', 'shortTitle'])(question),
                    locale: this.locale,
                    defaultLocale: survey.metadata.defaultLocale,
                    initialLocale: question.metadata.initialLocale,
                }) ?? '').trim() : '',
                questionDescription: question.content.longTitle ? (getQuestionTitle({
                    content: question.content.longTitle,
                    formContent: R.pathOr(null, ['formContent', 'longTitle'])(question),
                    locale: this.locale,
                    defaultLocale: survey.metadata.defaultLocale,
                    initialLocale: question.metadata.initialLocale,
                }) ?? '').trim() : '',
                questionLevel: this.getQuestionLevel(question),
                quantitative: this.getQuestionType(question) === 'Quantitative' ? 'Yes' : 'No',
                qualitative: this.getQuestionType(question) === 'Qualatitive' ? 'Yes' : 'No',
                imported: question.isDataImport ? 'Yes' : 'No',
                hasPrepopulatedData: question.hasPrepopulatedData ? 'Yes' : 'No',
                hidden: question.isHidden ? 'Yes' : 'No',
            }))
        this.filteredQuestions = this.questions
    }

    getQuestionLevel = (question) => {
        const oid = R.pathOr('', ['metadata', 'oid'], question)

        if (oid === 1) return 'Explori'
        if (oid < 7) return i18n.t('company')
        if (oid === 7) return i18n.t('survey')
        return ''
    }

    getQuestionType = (question) => {
        const { questionTypeCode } = question
        if (['NF', 'RD', 'CH', 'I', 'OD', 'PC', 'RT', 'TB'].includes(questionTypeCode)) return 'Quantitative'
        return 'Qualatitive'
    }

    buildCustomBenchmarkTagInfo = (benchmarkTags = []) => Object.fromEntries(
        // benchmark strings can't be reliably parsed (e.g. split on '-'), so we rebuild key string
        benchmarkTags.flatMap((tag) => {
            const config = tag.parentConfig || tag.customTagConfig
            if (!config) return []
            const parsedConfig = process.env.NEW_CUSTOM_BMTAGS_ENDPOINT ? config : JSON.parse(config)
            const scopes = parsedConfig.benchmarkOptions.filter((s) => s !== 'publish')
            return scopes.flatMap((scope) => tag.tagOptions.map((top) => {
                const toc = process.env.NEW_CUSTOM_BMTAGS_ENDPOINT
                    ? top.tagOptionConfig
                    : JSON.parse(top.tagOptionConfig)
                const tagOptionName = R.pathOr(
                    R.pathOr(R.pathOr('', ['langs', 'en_GB'], toc), ['langs', this.locale], toc),
                    ['name'], toc,
                )
                const key = `${scope} (${tag.customTagName} - ${tagOptionName})`
                return [key, { scope, label: `${tag.customTagName} - ${tagOptionName}` }]
            }))
        }),
    )

    getCustomBenchmarkTagInfo = (benchmark) => (
        this.customBenchmarkTagInfo?.[benchmark] ?? { label: benchmark, scope: undefined }
    )

    getBenchmarkLabel = (benchmark, v2BenchmarkData) => {
        let label = i18n.t(`report_${benchmark}_benchmark`)

        if (benchmark === 'sector') {
            const sectorName = R.pathOr('', ['sectorName'], v2BenchmarkData)
            label += sectorName ? ` (${sectorName})` : ''
        } else if (benchmark === 'portfolio') {
            const portfolioName = R.pathOr('', ['portfolioName'], v2BenchmarkData)
            label += portfolioName ? ` (${portfolioName})` : ''
        } else if (benchmark === 'division') {
            const divisionName = R.pathOr('', ['divisionName'], v2BenchmarkData)
            label += divisionName ? ` (${divisionName})` : ''
        } else if (benchmark === 'industry' && process.env.USE_BENCHMARKING_MODAL) {
            label = i18n.t('benchmarkModal.industry')
        }
        return label
    }

    getQueryParams = () => (
        window.location.search
            .substr(1)
            .split('&')
            .reduce((obj, param) => {
                const [k, v] = param.split('=')
                obj[k] = v
                return obj
            }, {})
    )

    toggleStillLoading = () => {
        this.stillLoading = !this.stillLoading
    }

    xAxisOptionTitle = (optionSet, questionTypeCode, option, initialLocale) => {
        const { survey } = this.appStore

        const label = getOptionTitle({
            langs: option.langs,
            locale: this.locale,
            defaultLocale: survey.metadata.defaultLocale,
            initialLocale,
        })

        const numericCode = (optionSet.hasNumericCodes || questionTypeCode === 'RT')
            ? option.reportingValue
            : undefined

        return `${label}${!R.isNil(numericCode) ? ` (${numericCode})` : ''}`
    }

    fetchMetrics = async (appStore) => {
        try {
            const response = await appStore.http.get('/metric')

            this.availableMetrics = response
                .reduce((map, metric) => map.set(metric.id, JSON.parse(metric.value)), new Map())
        } catch (e) {
            console.error(e)
        }
    }

    questionDetailsForExcel = (question, comparison, benchmark) => {
        try {
            const { reportData } = this
            const { survey } = this.appStore

            const {
                questionTypeCode,
                formId,
                pageQuestionId,
                questionId,
            } = question
            const dataKey = formId
                ? `${formId}_${questionId}`
                : pageQuestionId

            const data = reportData.questions[dataKey]

            const columns = ['TA', 'TX', 'OD', 'TL'].includes(questionTypeCode)
                ? []
                : [
                    {
                        title: i18n.t('report_question_responses'),
                        values: [],
                        plot: false,
                        kind: 'Int',
                        path: 'numberOfResponses',
                    },
                ]

            let nps
            let average

            let optionsY
            if (['RD', 'CH', 'SB', 'PC', 'OD', 'TL'].includes(questionTypeCode)) {
                optionsY = question.optionSet.options
            } else if (['TB', 'RT', 'SG'].includes(questionTypeCode)) {
                optionsY = question.optionSetY.options
            } else {
                optionsY = []
            }

            let options = optionsY.map((option) => (
                R.or(getOptionTitle({
                    langs: option.langs,
                    locale: this.locale,
                    defaultLocale: survey.metadata.defaultLocale,
                    initialLocale: question.metadata.initialLocale,
                }), '')
            ))

            if (questionTypeCode === 'OD') {
                Object.entries(data.xOptions).forEach(([title, d]) => {
                    columns.push({
                        title,
                        values: d.numberOfResponses,
                        plot: false,
                        colour: colours.getSerieColour('survey'),
                        kind: 'Int',
                        path: '',
                    })
                })

                columns.push({
                    title: 'Average',
                    values: data.aggregate.average,
                    plot: true,
                    colour: colours.getSerieColour('survey'),
                    kind: 'Double',
                    path: '',
                })
            }

            if (['RD', 'CH', 'SB', 'PC'].includes(questionTypeCode)) {
                const { hasNumericCodes } = question.optionSet
                const { kpiType } = question.metadata
                columns.push({
                    title: this.surveyTitle(),
                    values: [],
                    plot: true,
                    colour: colours.getSerieColour('survey'),
                    kind: 'Pct',
                    path: 'pctOfResponses',
                })
                if (hasNumericCodes) {
                    average = [
                        ['aggregate', 'average'],
                        ...comparison.map(({ path }) => `aggregate.${path}`.split('.')),
                        ...benchmark.map(({ path }) => `aggregate.${path}`.split('.')),
                    ].map((path) => R.pathOr(0, path, data))
                }
                if (kpiType === 'NPS' && hasNumericCodes) {
                    nps = [
                        ['aggregate', 'nps', 'survey'],
                        ...comparison.map(({ id }) => ['aggregate', 'nps', id]),
                        ...benchmark.map(({ id }) => ['aggregate', 'nps', id]),
                    ].map((path) => R.pathOr(0, path, data))
                }
            } else if (['I'].includes(questionTypeCode)) {
                options = data.yOptions.map(({ label }) => String(label))
                columns.push({
                    title: this.surveyTitle(),
                    values: [],
                    plot: true,
                    colour: colours.getSerieColour('survey'),
                    kind: 'Pct',
                    path: 'pctOfResponses',
                })
                average = [
                    ['aggregate', 'average'],
                ].map((path) => R.pathOr(0, path, data))
            } else if (['TB', 'RT'].includes(questionTypeCode)) {
                const hasNumericCodes = (question.optionSetX.hasNumericCodes || questionTypeCode === 'RT')
                question.optionSetX.options.forEach((o) => {
                    columns.push({
                        title: this.xAxisOptionTitle(
                            question.optionSetX,
                            questionTypeCode,
                            o,
                            question.metadata.initialLocale,
                        ),
                        values: [],
                        plot: !hasNumericCodes,
                        path: `xOptions.${o.id.toUpperCase()}.pctOfResponses`,
                        kind: 'Pct',
                    })
                })

                if (hasNumericCodes) {
                    columns.push({
                        title: 'Average',
                        values: [],
                        path: 'aggregate.average',
                        colour: colours.getSerieColour('survey'),
                        plot: true,
                        kind: 'Double',
                    })
                }
            } else if (['SG'].includes(questionTypeCode)) {
                question.optionSetX.options.forEach((o) => {
                    columns.push({
                        title: this.xAxisOptionTitle(
                            question.optionSetX,
                            questionTypeCode,
                            o,
                            question.metadata.initialLocale,
                        ),
                        values: [],
                        plot: false,
                        path: `xOptions.${o.id.toUpperCase()}.pctOfResponses`,
                        kind: 'Pct',
                    })
                })
                columns.push({
                    title: 'Average',
                    values: [],
                    path: 'aggregate.average',
                    colour: colours.getSerieColour('survey'),
                    plot: true,
                    kind: 'Double',
                })

                average = [
                    ['aggregate', 'average', 0],
                    ...benchmark.map(({ path }) => path.split('.')),
                ].map((path) => R.pathOr(0, path, data))
            } else if (['TA', 'TX', 'TL'].includes(questionTypeCode)) {
                const [ignoreA, ignoreB, numberOfResponses, questionOptions] = R.pathOr(() => [[], 'column'], ['questionSorting', dataKey], this)()
                options = questionOptions
                columns.push({
                    title: 'Total Responses',
                    values: numberOfResponses,
                    path: '',
                    colour: colours.getSerieColour('survey'),
                    plot: false,
                    kind: 'Int',
                })
            }

            return {
                options,
                questionColumns: columns,
                nps,
                average,
            }
        } catch (e) {
            console.log('error: ', e)
            if (e instanceof TypeError) {
                this.stillLoading = true
                return false
            }
        }
    }

    downloadQuestionExcel = (questionKey) => {
        this.questionKey = questionKey
        this.reportDownload('xlsx')
    }

    @action
    toggleAllPages = (isOpen) => {
        this.reportPages.forEach(({ id }) => this.openPages.set(id, isOpen))
    }

    initPageExpand = (pages) => {
        const openPages = pages.reduce((map, { id }) => map.set(id, true), new Map())
        this.openPages = new ObservableMap(openPages)
    }

    getDownloadKind = (downloadType) => {
        switch (downloadType) {
            case '3s':
                return 'tripleSExport'
            case 'spss':
                return 'spssExport'
            default:
                return 'surveyDataExport'
        }
    }

    getDownloadRequest = (downloadType, downloadOptions = {}, includeQuestions) => {
        const { appStore } = this
        const { survey, userData, tokenData } = appStore

        const kind = this.getDownloadKind(downloadType)
        const baseRequest = {
            kind,
            surveyId: survey.id.toUpperCase(),
            // token: surveyToken,
            userId: tokenData.userId,
            sessionId: userData.userId,
            state: this.getReportStates(),
            includeQuestions,
        }

        switch (downloadType) {
            case '3s':
                Object.assign(baseRequest, {
                    anonymize: R.pathOr(true, ['excludeRespondentMetadata'], downloadOptions),
                    langcode: this.locale,
                    startDate: this.dateFrom ? new Date(this.dateFrom).getTime() : undefined,
                })
                break
            case 'spss':
            case 'cm':
                Object.assign(baseRequest, {
                    clientId: tokenData.clientId,
                })
                break
            case 'csv':
                Object.assign(baseRequest, {
                    anonymize: R.pathOr(true, ['excludeRespondentMetadata'], downloadOptions),
                    includeColumnMetadata: R.pathOr(false, ['includeColumnMetadata'], downloadOptions),
                    numericExport: R.pathOr(false, ['numericExport'], downloadOptions),
                    delimiter: R.pathOr(false, ['delimiter'], downloadOptions),
                    langcode: this.locale,
                })
                break
            default:
        }
        return baseRequest
    }

    getChartImage = (eChart) => {
        if (!eChart) return ''
        const getBase64 = R.tryCatch(
            () => {
                const echartInstance = eChart.getEchartsInstance()
                return echartInstance.getDataURL({ type: 'png', backgroundColor: '#FFF' })
            },
            () => null,
        )
        const base64 = getBase64()
        return base64 ? `<img src="${base64}" alt="chart" style="width: 100%; height: auto;"/>` : ''
    }

    generateReportStatsElements = () => {
        const reportStatsChartEl = this.questionChartRefs.get('reportStats')
        const chartImg = this.getChartImage(reportStatsChartEl)
        const { survey } = this.appStore
        return survey.isMaxbi() ? '<div class="panel filter" />' : `<div class="panel filter">
                ${document.getElementById('questionData_stats').outerHTML}
                ${chartImg}
            </div>`
    }

    generatePdfData = (reportVerbatim, includeQuestions) => {
        const { appStore } = this
        const { survey } = appStore
        const firstPageReportStats = this.generateReportStatsElements()
        const html = this.reportPages.reduce((innerHtml, page) => {
            innerHtml += R.pathOr([], ['questions'])(page)
                .filter(({ id }) => !includeQuestions || includeQuestions.includes(id))
                .reduce((questionHtml, question) => {
                    const { questionTypeCode } = question
                    const dataElem = document.getElementById(`questionData_${question.pageQuestionId.toUpperCase()}`)
                    const titleElem = document.getElementById(`questionTitle_${question.pageQuestionId.toUpperCase()}`)
                    const eChart = this.questionChartRefs.get(`pageQuestion_${question.pageQuestionId}`)
                    const chartImg = this.getChartImage(eChart)

                    const dataOuterHtml = dataElem && !['TX', 'TA', 'TL'].includes(questionTypeCode)
                        ? dataElem.outerHTML
                        : ''

                    const titleInnerHtml = titleElem
                        ? titleElem.innerHTML
                        : ''

                    const verbatim = reportVerbatim || (['TA', 'TX', 'TL', 'DT'].includes(questionTypeCode) && !!includeQuestions)
                        ? R.pathOr(
                            () => '',
                            ['verbatim', question.pageQuestionId.toUpperCase()],
                        )(this)()
                        : ''

                    return `
                        ${questionHtml}
                        <div class="panel filter">
                            ${titleInnerHtml}
                            ${dataOuterHtml}
                            ${verbatim}
                            ${chartImg}
                        </div>
                    `
                }, '')
            return innerHtml
        }, firstPageReportStats)

        return {
            surveyId: survey.id,
            surveyTitle: survey.title,
            productName: survey.metadata.productInfo.industrySpecificInfo.productName,
            html,
        }
    }

    generateExcelData = (questionKey, includeQuestions) => {
        try {
            const { reportData, benchmarks2, yoyComparisons } = this
            const { survey } = this.appStore

            const benchmarkCols = Array.from(benchmarks2)
                .filter(([_, v]) => v)
                .reduce((arr, [key]) => {
                    arr.push({
                        id: key,
                        title: i18n.t(`report_${key}_benchmark`),
                        values: [],
                        plot: true,
                        colour: colours.getSerieColour(key),
                        kind: 'Pct',
                        path: `benchmarks.${key}`,
                    })
                    return arr
                }, [])

            const comparisonCols = Array.from(yoyComparisons)
                .map(([id, { label }]) => ({
                    id: id.toLowerCase(),
                    title: this.comparisonTitle(label),
                    path: `comparison.${id.toUpperCase()}`,
                    kind: 'Pct',
                    plot: true,
                    colour: colours.getSerieColour('comparison'),
                }))

            const q = this.reportPages.reduce((reportQuestions, page) => {
                const reportPageQuestions = page.questions
                    .filter(({ questionTypeCode, id }) => ['RD', 'CH', 'TB', 'RT', 'SB', 'PC', 'SG', 'I', 'TA', 'TX', 'OD', 'TL'].includes(questionTypeCode) && (!includeQuestions || includeQuestions.includes(id)))
                    .filter(({ formId, questionId, pageQuestionId }) => (
                        !R.isNil(questionKey)
                            ? ((formId ? `${formId}_${questionId}` : pageQuestionId) === questionKey)
                            : true
                    ))
                    .reduce((pageQuestions, question) => {
                        const {
                            formId,
                            pageQuestionId,
                            questionId,
                            questionTypeCode,
                        } = question
                        const dataKey = formId
                            ? `${formId}_${questionId}`
                            : pageQuestionId

                        const data = reportData.questions[dataKey]

                        const isBenchmarked = R.pathOr(false, ['metadata', 'isBenchmarked'], question)
                        const comparison = cloneDeep(comparisonCols)
                            .map((c) => {
                                const comp = cloneDeep(c)
                                if (['TB', 'RT', 'OD'].includes(questionTypeCode)) {
                                    comp.path = `aggregate.${comp.path}`
                                    comp.kind = 'Double'
                                }
                                return comp
                            })
                            .filter(({ path }) => (R.hasPath([...path.split('.')], data)))
                        const benchmark = cloneDeep(benchmarkCols)
                            .map((b) => {
                                const comp = cloneDeep(b)
                                if (questionTypeCode === 'SG') {
                                    comp.kind = 'Empty'
                                    comp.path = `aggregate.${comp.path}`
                                    comp.plot = false
                                } else if (['TB', 'RT', 'OD'].includes(questionTypeCode)) {
                                    comp.path = `aggregate.${comp.path}`
                                    comp.kind = 'Double'
                                }
                                return comp
                            })
                            .filter(({ path }) => (isBenchmarked && R.hasPath([...path.split('.')], data)))

                        const {
                            options,
                            questionColumns,
                            nps,
                            average,
                        } = this.questionDetailsForExcel(question, comparison, benchmark)

                        const [sortedIndexArray, chartType] = R.pathOr(() => [[], 'column'], ['questionSorting', dataKey], this)()
                        const columns = [
                            ...questionColumns,
                            ...comparison,
                            ...benchmark,
                        ].map((col) => {
                            if (!['TX', 'TA', 'OD', 'TL'].includes(questionTypeCode)) {
                                col.values = sortedIndexArray.map((i) => (
                                    col.kind === 'Pct'
                                        ? (R.pathOr(0, [...col.path.split('.'), i], data) / 100)
                                        : R.pathOr(0, [...col.path.split('.'), i], data)
                                ))
                            }

                            return col
                        })

                        const request = {
                            title: getQuestionTitle({
                                content: question.content.shortTitle,
                                formContent: R.pathOr(null, ['formContent', 'shortTitle'])(question),
                                locale: this.locale,
                                defaultLocale: survey.metadata.defaultLocale,
                                initialLocale: question.metadata.initialLocale,
                            }).trim(),
                            description: R.or(getQuestionTitle({
                                content: question.content.longTitle,
                                formContent: R.pathOr(null, ['formContent', 'longTitle'])(question),
                                locale: this.locale,
                                defaultLocale: survey.metadata.defaultLocale,
                                initialLocale: question.metadata.initialLocale,
                            }), '').trim(),
                            questionType: questionTypeCode,
                            additional: [],
                            subOptions: [],
                            kpi: R.pathOr('', ['metadata', 'kpiType'], question),
                            chartType,
                            options: sortedIndexArray.map((i) => options[i]),
                            columns,
                            nps,
                            average,
                        }
                        pageQuestions.push(request)
                        return pageQuestions
                    }, [])
                reportQuestions.push(...reportPageQuestions)
                return reportQuestions
            }, [])

            const completionStats = R.isNil(questionKey)
                ? [{
                    title: 'Survey Stats',
                    chartType: 'column',
                    description: '',
                    questionType: '',
                    kpi: '',
                    additional: [],
                    subOptions: [],
                    columns: [
                        {
                            title: 'Completed',
                            values: reportData.stats.details.complete,
                            plot: true,
                            colour: colours.getSerieColour('survey'),
                            kind: 'Int',
                        },
                        {
                            title: 'Dropped',
                            values: reportData.stats.details.drop,
                            plot: true,
                            colour: colours.getSerieColour('dropped'),
                            kind: 'Int',
                        },
                        {
                            title: 'Screened',
                            values: reportData.stats.details.screen,
                            plot: true,
                            colour: colours.getSerieColour('screened'),
                            kind: 'Int',
                        },
                    ],
                    options: this.reportPages.map((page) => (
                        MergeFields.replace(
                            page.title,
                            this.locale,
                            this.mergeFields,
                        )
                    )),
                }] : []

            return [...completionStats, ...q]
        } catch (e) {
            console.log('error: ', e)
            if (e instanceof TypeError) {
                this.stillLoading = true
                return false
            }
        }
    }

    resetToDefaultFilters = () => {
        this.reportFilters = []
        this.reportSurveyDataIds = []
        this.surveyDataIds = ''
        this.toDate = undefined
        this.fromDate = undefined
        this.campaigns = []
        this.benchmarks = new Map(Array.from(this.benchmarks).map(([k]) => ([k, ['industry', 'company'].includes(k)])))
        this.years = this.defaultComparisonSurvey
            ? [{
                id: this.defaultComparisonSurvey.value,
                year: this.defaultComparisonSurvey.year,
            }]
            : [{
                id: '',
                year: 0,
            }]

        this.states = new Map(Array.from(this.states).map(([k]) => ([k, ['completed'].includes(k)])))
        this.filterReset = new Date().getTime()
        this.reloadData()
    }

    constructor(locale, surveyId, clientId, appStore) {
        makeObservable(this)
        const queryParams = this.getQueryParams()
        this.fetchMetrics(appStore)

        this.states.set('test', (queryParams.testdata === 'true'))
        this.states.set('incomplete', (queryParams.includeIncompletes === 'true'))

        if (queryParams.testdata === 'true') {
            this.selectedResponseStatuses.set('test', this.responseStatuses.find(({ value }) => value === 'test'))
        }

        if (queryParams.filter) {
            this.reportFilters = queryParams.filter.split(',')
        }

        this.appStore = appStore
        this.showYears = appStore.survey.metadata.taxonomy.surveyType === 'PE'
        this.locale = queryParams.langcode || locale
        this.surveyId = surveyId
        this.clientId = clientId

        if (appStore.survey.metadata.isRapidSurvey) {
            this.benchmarkPeriods[1].disabled = true
        }
    }

    toggleIncludeTest = () => {
        this.states.set('test', !this.states.get('test'))
        this.reloadData()
    }

    toggleIncludeIncomplete = () => {
        this.states.set('incomplete', !this.states.get('incomplete'))
        this.reloadData()
    }

    scrollTop = () => {
        window.scrollTo({ top: 0 })
    }

    getBenchmarks = async () => {}

    reportDownload = (type, verbatim = false) => {
        this.reportDownloadType = type
        this.reportVerbatim = verbatim
        this.appStore.downloadModalOpen = true
    }

    getCampaignList = async () => {
        const { appStore } = this
        const { survey } = appStore
        const campaignList = await appStore.http.get(`/survey/${survey.id}/getCampaignList`)
        this.campaignList = R.groupBy(R.prop('type'), campaignList.map((campaign) => ({
            id: campaign.campaignid,
            label: campaign.campaigntitle,
            type: campaign.campaignType,
        })))
    }

    reloadComparison = async (idToDelete) => {
        this.isLoading = true
        if (R.isNil(idToDelete)) {
            await this.getComparison()
        } else {
            Object.keys(this.comparison).forEach((questionId) => {
                Object.deleteProperty(idToDelete.toUpperCase(), this.comparison[questionId])
            })
        }
        this.formatReportData()
        this.reportDataFetched = new Date().getTime()
        this.isLoading = false
    }

    reloadData = async () => {
        this.isLoading = true
        await Promise.all([
            this.getReportData(),
            ...(process.env.USE_COMPARISON_FILTER ? [this.getComparison()] : []),
        ])
        this.formatReportData()
        this.reportDataFetched = new Date().getTime()
        setTimeout(() => { this.isLoading = false }, 500)
    }

    filterResponseData = () => {
        this.reloadData()
    }

    clearFilters = () => {
        this.surveyDataIds = ''
        this.reportFilters = []
        this.reloadData()
    }

    useVerbatimFilter = (surveyDataIds, verbatim) => {
        this.verbatim = verbatim.length > 30 ? `${verbatim.substr(0, 27)}...` : verbatim
        this.surveyDataIds = surveyDataIds
        this.reloadData()
    }

    removeVerbatim = () => {
        this.verbatim = ''
        this.surveyDataIds = ''
        this.reloadData()
    }

    stripHtml = (html) => {
        const doc = this.parser.parseFromString(html, 'text/html')
        return doc.body.textContent || ''
    }

    useFilter = (e) => {
        const filterId = e.target.getAttribute('data-value')
        const filterIndex = this.reportFilters.indexOf(filterId)
        if (filterIndex === -1) {
            this.reportFilters.push(filterId)
            this.reloadData()
        }
    }

    removeFilter = (filterId) => {
        const filterIndex = this.reportFilters.indexOf(filterId)
        if (filterIndex > -1) {
            this.reportFilters.splice(filterIndex, 1)
            this.reloadData()
        }
    }

    removeXTab = (e) => {
        const id = e.target.getAttribute('id')
        const index = this.xTabs.indexOf(id)

        if (index > -1) {
            this.xTabs.splice(index, 1)
        }
    }

    toggleDebug = (e) => {
        e.stopPropagation()
        this.showDebug = !this.showDebug
    }

    @action
    editResponse = (url) => {
        this.editAnswersURL = url
        this.editAnswersModalIsOpen = true
    }

    toggleCampaigns = (id) => {
        this.campaigns = [id]
    }

    toggleYears = (id) => {
        this.years = [id]
    }

    updateURL = () => {
        const url = `${window.location.origin}${window.location.pathname}`
        const filterObj = {
            langcode: this.locale,
            filter: this.reportFilters,
            startDate: this.fromDate,
            endDate: this.toDate,
            testdata: this.states.get('test'),
            includeIncompletes: this.states.get('incomplete'),
            campaignfilter: this.campaigns[0],
            comparefilter: this.years[0].id,
            surveyDataId: this.surveyDataIds,

        } // tmp rubbish
        const queryString = Object.keys(filterObj)
            .filter((k) => filterObj[k] !== undefined && filterObj[k] !== '')
            .reduce((qs, k, i) => {
                qs += `${i > 0 ? '&' : ''}${k}=${filterObj[k]}`
                return qs
            }, '')
        window.history.pushState({ }, document.title, `${url}?${queryString}`)
    }

    getMergeFields = async () => {
        try {
            this.mergeFields = await this.appStore.http.get(`/mergefields/survey/${this.surveyId}`)
        } catch (e) {
            console.log(`mergeFields error -> ${e}`)
            this.mergeFields = {}
        }
    }

    getEMSurveyUser = async () => {
        const { survey } = this.appStore
        const { id } = survey
        try {
            if (survey.metadata.taxonomy.surveyType === 'EM') {
                const surveyUserRes = await this.appStore.http.get(`/surveyusers/${id}?top=1&start=0&sdstatus=&optedout=false&invalid=false&type=1`)
                this.emSurveyUser = R.pathOr(undefined, ['data', 0, 'surveyuserid_txt'], surveyUserRes)
            } else {
                this.emSurveyUser = undefined
            }
        } catch (e) {
            this.emSurveyUser = undefined
        }
    }

    getV2Stats = async () => {
        const { appStore, v2ReportStats } = this

        let response = {
            browser: {},
            device: {},
            stats: {},
        }

        try {
            if (R.isNil(v2ReportStats)) {
                response = await appStore.http.get(`/report/v1/${this.surveyId}/stats`)
            } else {
                response = v2ReportStats
            }
        } catch (e) {
            console.error('Error getting survey stats', e)
        }
        return response
    }

    changeBenchmarkPeriod = async ({ period }) => {
        const { appStore } = this

        this.benchmarkPeriod = period
        this.isLoading = true

        Reflect.deleteProperty(this, 'v2BenchmarkData')
        let v2Benchmarks = {}
        try {
            v2Benchmarks = await this.getV2Benchmarks()
        } catch (e) {
            console.error('Error reloading benchmarks', e)
            appStore.addNotification(null, 'Error getting benchmarks', 'error')
        }
        this.v2BenchmarkData = R.or(v2Benchmarks, {})
        this.formatReportData()
        this.reportDataFetched = new Date().getTime()
        this.isLoading = false
    }

    getBenchmarkTags = async () => {
        if (process.env.USE_CUSTOM_BENCHMARKS !== 'true') return []
        const { appStore } = this
        const { survey } = appStore
        let response
        if (process.env.NEW_CUSTOM_BMTAGS_ENDPOINT) {
            const query = `?productId=${survey.metadata.productInfo.productId}`
            response = await appStore.http.get(`/customBenchmarkTags${query}`)
        } else {
            response = await appStore.http.get('/benchmarkTags')
        }
        return response.benchmarkTags
    }

    getV2Benchmarks = async () => {
        const { appStore, v2BenchmarkData } = this
        const { survey } = appStore
        const { surveyType } = survey.metadata.taxonomy
        let response = {
            benchmarks: {},
        }

        if (!R.isNil(v2BenchmarkData)) return v2BenchmarkData

        try {
            const { period, duration } = this.benchmarkPeriod
            const queryParams = `?period=${period}${!R.isNil(duration) ? `&duration=${duration}` : ''}`

            // loads benchmarks only for benchmarking or Maxbi surveys
            this.benchmarkOptions = []
            response = ['PE', 'EM'].includes(surveyType)
                ? await appStore.http.get(`/report/v1/${this.surveyId}/benchmark${queryParams}`) // testBechmark.default
                : {}
            const userBenchmarks = R.pathOr({}, ['availableBenchmarks'], response)
            const customBenchmarks = R.pathOr([], ['customBenchmarks'], response)
            const benchmarks = ['industry', 'company', 'division', 'portfolio', 'sector']
            const isMultiDivision = R.pathOr(false, ['userData', 'flags', 'isMultiDivision'], appStore)
            benchmarks
                .filter((b) => userBenchmarks[b])
                .filter((b) => (
                    b === 'division'
                        ? isMultiDivision
                        : true
                ))
                .forEach((b) => {
                    const obj = {
                        value: b,
                        label: this.getBenchmarkLabel(b, response),
                    }
                    if (['industry', 'company'].includes(b)) {
                        this.benchmarks2.set(b, obj)
                    }
                    this.benchmarkOptions.push(obj)
                })
            if (process.env.USE_CUSTOM_BENCHMARKS === 'true') {
                this.benchmarkTags = await this.getBenchmarkTags()
                this.customBenchmarkTagInfo = this.buildCustomBenchmarkTagInfo(this.benchmarkTags)
                customBenchmarks
                    // filter custom benchmarks outside of user scope permission
                    .filter((cbm) => userBenchmarks[this.getCustomBenchmarkTagInfo(cbm).scope])
                    .forEach((cbm) => {
                        const obj = {
                            value: cbm,
                            label: this.getCustomBenchmarkTagInfo(cbm).label,
                        }
                        this.benchmarkOptions.push(obj)
                    })
            }
        } catch (e) {
            console.error('error getting benchmarks', e)
            appStore.addNotification(null, 'Error getting benchmarks', 'error')
        }

        return response
    }

    getReportFilters = () => {
        const reportFilters = R.pathOr([], ['reportFilters'])(this).reduce((filters, filter) => {
            const [key, set1, set2] = filter.split('_')
            const [pageQuestionId, questionId] = key.split('|')
            let filterKey = questionId
            if (R.isNil(questionId)) {
                filterKey = R.pathOr(new Map(), ['pageQuestionMap'])(this).has(pageQuestionId.toUpperCase())
                    ? this.pageQuestionMap.get(pageQuestionId.toUpperCase()).id
                    : pageQuestionId
            }

            if (!R.path([filterKey], filters)) {
                filters[filterKey] = []
            }

            filters[filterKey].push(`${set1}${set2 ? `|${set2}` : ''}`)

            return filters
        }, {})

        return Object.keys(reportFilters).length > 0
            ? reportFilters
            : undefined
    }

    getCampaignFilter = () => {
        let campaignId
        let userListId
        if (!R.isNil(this.campaigns[0]) && !R.isEmpty(this.campaigns[0])) {
            const [type, id] = this.campaigns[0].split('_')
            if (type === '0') {
                campaignId = 0
                userListId = '00000000-0000-0000-0000-000000000000'
            } else if (type === 'campaign') {
                campaignId = parseInt(id, 10)
            } else if (type === 'email') {
                userListId = id
            }
        }

        return {
            campaignId,
            userListId,
        }
    }

    getFilterDate = (dateKey) => {
        // expected format yyyy-mm-ddTHH:ii:ssZ
        const ruleGroup = this.filters.responseDate
        const existingRule = ruleGroup.findRule(dateKey)
        const existingDate = R.pathOr(undefined, ['options', 0, 'label'], existingRule)
        return !R.isNil(existingDate)
            ? `${existingDate.split('/').reverse().join('-')}T00:00:00Z`
            : undefined
    }

    getV1Report = async () => {
        const { appStore } = this
        const { survey } = appStore

        let reportData = {}
        try {
            const reportFilters = R.pathOr([], ['reportFilters'])(this).reduce((filters, filter) => {
                const [key, set1, set2] = filter.split('_')
                const [pageQuestionId, questionId] = key.split('|')
                let filterKey = questionId
                if (R.isNil(questionId)) {
                    filterKey = R.pathOr(new Map(), ['pageQuestionMap'])(this).has(pageQuestionId.toUpperCase())
                        ? this.pageQuestionMap.get(pageQuestionId.toUpperCase()).id
                        : pageQuestionId
                }

                if (!R.path([filterKey], filters)) {
                    filters[filterKey] = []
                }

                filters[filterKey].push(set1)

                return filters
            }, {})

            reportData = this.appStore.http.post(`/v1/survey/${this.surveyId}/report`, JSON.stringify({
                langcode: this.locale,
                state: this.getReportStates(),
                includeTestResults: this.selectedResponseStatuses.has('test'),
                comparison: this.getReportComparisons(),
                filter: Object.keys(reportFilters).length > 0
                    ? reportFilters
                    : undefined,
                surveyDataIds: this.surveyDataIds.length > 0
                    ? this.surveyDataIds.split(',')
                    : undefined,
            }), this.appStore.surveyToken)
        } catch (e) {
            console.error('getV1Report', e)
            appStore.addNotification(null, 'Error getting report', 'error')
        }
        return reportData
    }

    saveFilter = (filterKey, ruleGroup) => {
        this.filters[filterKey] = RuleGroup.clone(ruleGroup)
        this.showFilters = false
        this.reloadData()
    }

    discardAllFilters = () => {
        this.filters = {
            question: new RuleGroup(RuleGroup.getDefaultLogic('question')),
            campaign: new RuleGroup(RuleGroup.getDefaultLogic('campaign')),
            responseDate: new RuleGroup(RuleGroup.getDefaultLogic('responseDate')),
            openEnded: new RuleGroup(RuleGroup.getDefaultLogic('openEnded')),
        }
        this.reloadData()
    }

    getSurveyDataIdFilter = (filterGroup) => Array.from(filterGroup.rules.reduce((set, { options }) => {
        (options ?? []).forEach(({ surveyDataIds }) => {
            if (Array.isArray(surveyDataIds)) {
                surveyDataIds.forEach((id) => set.add(id))
            } else {
                set.add(surveyDataIds)
            }
        })
        return set
    }, new Set()))

    getV2Report = async () => {
        const { appStore } = this

        let reportData = {}
        try {
            reportData = await appStore.http.post(
                `/report/v1/${this.surveyId}`,
                JSON.stringify({
                    filter: {
                        state: this.getReportStates(),
                        surveyDataIds: this.getSurveyDataIdFilter(this.filters.openEnded),
                        ...R.omit(['openEnded'], this.filters),
                    },
                    // useCache: R.pathOr(false, ['userData', 'flags', 'useReportCache'], appStore),
                }),
                appStore.surveyToken,
            )
        } catch (e) {
            console.error('getV2Report', e)
            appStore.addNotification(null, 'Error getting report', 'error')
        }
        return reportData
    }

    getCrossTabs = async () => {
        const { appStore } = this
        const { surveyToken } = appStore
        const xTabs = await appStore.http.get(`/survey/${this.surveyId}/advancedxtab`, surveyToken)
        this.xTabs = xTabs
    }

    deleteCrossTab = async (xTabId) => {
        const { appStore, xTabs } = this
        const { surveyToken } = appStore

        try {
            await appStore.http.delete(`/survey/${this.surveyId}/advancedxtab/${xTabId}`, surveyToken)
            appStore.addNotification(null, 'Advanced cross tab removed', 'success')
            this.xTabs = xTabs.filter(({ id }) => id !== xTabId)
        } catch (e) {
            appStore.addNotification(null, 'Error removing advanced cross tab', 'error')
        }
    }

    setMaxbiStats = (data, v2Stats) => {
        const { v2ReportStats, appStore } = this
        const { survey } = appStore
        if (!R.isNil(v2ReportStats)) return v2ReportStats

        try {
            const { questions } = data
            const completions = questions.reduce((map, { questionId, responses }) => ({
                ...map,
                [questionId]: R.or(responses, []).some((r) => r > 0),
            }), {})
            const getPageQuestionIds = (pageQuestions) => (
                pageQuestions.filter(({ id: pageQuestionId }) => {
                    const question = this.pageQuestionMapNew.get(pageQuestionId.toUpperCase())
                    return question.questionTypeCode !== 'ST'
                }).map(({ id: pageQuestionId, questionId }) => {
                    const question = this.pageQuestionMapNew.get(pageQuestionId.toUpperCase())
                    if (question.questionTypeCode === 'FM') {
                        return question.optionSet.map(({ question }) => question.id)
                    }

                    return [questionId]
                }).flat()
            )
            v2Stats.stats.details.complete = survey.standardPages.reduce((map, { id, questions }) => ({
                ...map,
                [id]: getPageQuestionIds(R.or(questions, [])).every((questionId) => completions[questionId]) ? 1 : 0,
            }), {})
        } catch (e) {
            console.error('setMaxbiStats', e)
        }
        return v2Stats
    }

    getReportData = async () => {
        const { appStore } = this
        const { survey } = appStore

        try {
            this.updateURL()

            const [v2Data, v2Stats, v2Benchmarks] = await Promise.all([
                this.getV2Report(),
                this.getV2Stats(),
                this.getV2Benchmarks(),
            ])
            this.v2ReportData = v2Data
            this.v2ReportStats = survey.isMaxbi() ? this.setMaxbiStats(v2Data, v2Stats) : v2Stats
            this.v2BenchmarkData = R.or(v2Benchmarks, {})
            this.isReportDataReady = true

            this.surveyStatus = appStore.getStatus(R.pathOr(0, ['stats', 'aggregate', 'total', 'complete'], v2Stats))
        } catch (e) {
            console.log(`Error getting getReportData -> ${JSON.stringify(e, null, 4)}`)
        }
    }

    setYoyComparison = () => {
        const { appStore } = this
        const { survey, lastYearSurveys } = appStore

        this.lastYearSurveys = lastYearSurveys.slice().map(({ value, year }) => ({
            value,
            label: year,
        }))

        const surveyYear = survey.getYear()
        const lastYear = surveyYear - 1

        const defaultYOY = R.pathOr('', ['metadata', 'defaultYrOnYrSurveyId'], survey).toUpperCase()
        const yoyComparison = this.lastYearSurveys.find(({ value }) => (
            value === defaultYOY
        ))
        const comparisonSurvey = yoyComparison || this.lastYearSurveys.find(({ label }) => (
            label === lastYear
        ))

        if (comparisonSurvey) {
            this.yoyComparisons.set(comparisonSurvey.value, comparisonSurvey)
        }
        this.defaultComparisonSurvey = comparisonSurvey
        this.surveyYear = surveyYear
        // this.years = comparisonSurvey ? [{
        //     id: comparisonSurvey.value,
        //     year: comparisonSurvey.year,
        // }] : [{
        //     id: '',
        //     year: 0,
        // }]
    }

    setSurveyBranchComparisons = () => {
        const { appStore } = this
        const { survey } = appStore
        if (this.showBranches) {
            survey.surveyBranches.forEach((b) => {
                this.branchComparisons.set(b.surveyId, { value: b.surveyId, label: b.name })
            })
        } else {
            this.branchComparisons.clear()
        }
    }

    toggleShowBranches = (newVal) => {
        this.showBranches = newVal !== undefined ? newVal : !this.showBranches
        this.setSurveyBranchComparisons()
    }

    setFilterStatuses = () => {
        const { appStore } = this
        this.responseStatuses = [
            {
                value: 'completed',
                label: 'Completed',
            },
            {
                value: 'dropped',
                label: 'Dropped',
            },
            {
                value: 'screened',
                label: 'Screened',
            },
            {
                value: 'test',
                label: 'Test',
            },
            {
                value: 'notStarted',
                label: i18n.t('report.filter.notStarted'),
                show: appStore.userData.flags.isRegistrationFilterEnabled && this.hasPrepopulatedData,
            },
        ].filter(({ show }) => (
            R.isNil(show) || show
        ))
    }

    checkPrepopulated = () => {
        const { appStore } = this
        const { survey } = appStore
        for (const { questions } of survey.pages) {
            if (questions && questions.some(({ hasPrepopulatedData }) => hasPrepopulatedData)) {
                this.hasPrepopulatedData = true
                break
            }
        }

        console.log('checkPrepopulated', this.hasPrepopulatedData)
    }

    formatReportData = () => {
        try {
            const pageQuestionMap = new Map()
            const questionMap = new Map()
            this.reportPages.forEach((rp) => {
                if (rp.questions) {
                    rp.questions.forEach((q) => {
                        pageQuestionMap.set(q.pageQuestionId, q)
                        questionMap.set(q.questionId.toUpperCase(), q)
                    })
                }
            })
            const questions = this.reportPages.map((rp) => {
                if (!rp.questions) return []

                return rp.questions
            }).flat()
            let v2Data = {}
            try {
                v2Data = formatV2(this.v2ReportData, this.v2ReportStats, questions, this.reportPages, this.surveyId, R.pathOr({}, ['v2BenchmarkData', 'benchmarks'], this), this.comparison)
            } catch (e) {
                console.log('v2', e)
            }
            this.reportData = v2Data
            this.reportDataV2 = v2Data
        } catch (e) {
            console.log(`Error in formatReportData -> ${e}`)
        }
    }

    changeAreaUnit = () => {
        const { areaUnit } = this
        this.areaUnit = areaUnit === 'M2' ? 'FT2' : 'M2'
    }

    getReportStates = () => Array.from(this.selectedResponseStatuses).map(([state]) => state)

    getReportComparisons = () => {
        const comparisons = Array.from(this.yoyComparisons).map(([surveyId]) => surveyId)
        return comparisons.length === 0 ? undefined : comparisons
    }

    toQueryString(includeLangCode = true) {
        let filter = includeLangCode ? `langcode=${this.locale}&` : ''
        filter += `surveyId=${this.surveyId}&surveyDataId=${this.surveyDataIds}&filter=${this.reportFilters || ''}&startDate=${this.fromDate || ''}&endDate=${this.toDate || ''}&testdata=${this.states.get('test')}&includeIncompletes=${this.states.get('incomplete')}&campaignfilter=${this.campaigns[0] || ''}&comparefilter=${this.years[0].id || ''}&isPdfReport=false&clientID=${this.clientId}&includeothertext=1`
        return filter
    }

    comparisonTitle = (year) => (
        this.showYears
            ? `${i18n.t('report_question_comparisons')} (${year})`
            : i18n.t('report_question_comparisons')

    )

    surveyTitle = () => (
        this.showYears
            ? `${i18n.t('report_question_survey')} (${this.surveyYear})`
            : i18n.t('report_question_survey')
    )

    getTextData = async (questionId, questionOptionId, questionTypeCode) => {
        let response = ''
        try {
            const reportFilters = R.pathOr([], ['reportFilters'])(this).reduce((filters, filter) => {
                const [key, set1, set2] = filter.split('_')
                const [pageQuestionId, questionId] = key.split('|')
                let filterKey = questionId
                if (R.isNil(questionId)) {
                    filterKey = R.pathOr(new Map(), ['pageQuestionMap'])(this).has(pageQuestionId.toUpperCase())
                        ? this.pageQuestionMap.get(pageQuestionId.toUpperCase()).id
                        : pageQuestionId
                }

                if (!R.path([filterKey], filters)) {
                    filters[filterKey] = []
                }

                filters[filterKey].push(set1)

                return filters
            }, {})
            response = await this.appStore.http.post(
                `/report/v1/${this.surveyId}/textquestion`,
                JSON.stringify({
                    questionId,
                    questionOptionId,
                    questionType: questionTypeCode,
                    filter: {
                        state: this.getReportStates(),
                        ...this.filters,
                    },
                }),
                this.appStore.surveyToken,
            )
        } catch (e) {
            console.log(`Error getting getReportData -> ${e}`)
        }
        return response
    }

    sortDetails = (dataKey, col, options, locales) => {
        const data = this.reportData.questions[dataKey]
        let values
        let sortBy
        if (!R.hasPath(['path'], col)) {
            sortBy = 'label'
            values = options.reduce((newOptions, o) => {
                newOptions.push(getOptionTitle({
                    langs: o.langs,
                    locale: locales.locale,
                    defaultLocale: locales.defaultLocale,
                    initialLocale: locales.initialLocale,
                }))
                return newOptions
            }, [])
        } else {
            sortBy = col.path
            values = R.pathOr(0, col.path.split('.'))(data)
        }
        return {
            values,
            sortBy,
        }
    }

    // filter only selected reponses for maxbi survey
    filterResponses = (dataKey, question) => {
        const { reportData } = this
        const data = R.pathOr({}, ['questions', dataKey], reportData)
        const { questionTypeCode } = question
        if (['TB', 'RT'].includes(questionTypeCode)) {
            return Object.keys(R.pathOr([], ['yOptions'], data)).filter((key) => (R.pathOr(0, ['numberOfResponses', key], data) > 0))
        }
        // if (questionTypeCode === 'OD') {
        //     return Object.keys(R.pathOr([], ['xOptions'], data)).filter(
        //         (key) => !!data.xOptions[key].numberOfResponses.find(
        //             (numberOfResponse) => (numberOfResponse === 1),
        //         ),
        //     )
        // }
        if (!['ST', 'FM'].includes(questionTypeCode)) {
            return R.pathOr([], ['numberOfResponses'], data).reduce(
                (accum, numberOfResponse, index) => accum.concat(
                    numberOfResponse === 1 ? index : [],
                ), [],
            )
        }
        return []
    }

    // show chart if not Maxbi survey
    showChart = (question) => {
        const { questionTypeCode } = question
        if (['PC', 'NF'].includes(questionTypeCode)) return true
        return false
    }

    // convert currency for maxbi NumberForm reports
    convertCurrency = (value) => (
        parseFloat((value * this.exchangeRates[this.currentCurrency]).toFixed(2))
    )

    getComparison = async () => {
        const { appStore } = this
        const { survey } = appStore
        const { surveyType } = survey.metadata.taxonomy

        let comparison = {}

        if (surveyType === 'EM') {
            this.comparison = comparison
            return
        }

        try {
            const comparisonIds = Array.from(this.yoyComparisons).map(([surveyId]) => surveyId)
            if (survey.metadata.isParent && survey.surveyBranches) {
                comparisonIds.push(...survey.surveyBranches.map((b) => b.surveyId))
            }
            const body = JSON.stringify({
                comparison: comparisonIds,
                ...(process.env.USE_COMPARISON_FILTER ? {
                    filter: {
                        state: this.getReportStates(),
                        surveyDataIds: this.getSurveyDataIdFilter(this.filters.openEnded),
                        ...R.omit(['openEnded'], this.filters),
                    },
                } : {}),
            })
            comparison = await appStore.http.post(`/report/v1/${this.surveyId}/comparison`, body, appStore.surveyToken)
        } catch (e) {
            console.error(e)
            appStore.addNotification(null, 'Error loading report comparison', 'error')
        }

        this.comparison = comparison
    }

    getExchangeRates = async () => {
        const { appStore } = this
        const { langcode } = appStore
        const { defaultCurrency, rates } = await appStore.http.get(`/report/v1/${this.surveyId}/exchangerates`, appStore.surveyToken)
        this.exchangeRates = rates
        this.currentCurrency = defaultCurrency
        this.currencySign = Object.keys(rates).reduce((signs, rate) => (
            { ...signs, [rate]: getCurrencySymbol(rate, langcode) }
        ), {})
    }

    changeCurrentCurrency = (value) => {
        this.currentCurrency = value
    }

    /** Quick filtering */
    @observable showQuickFilter = false

    @observable showQuickFilterInfo = true

    @observable quickFilters = new Set()

    quickFilterGroups

    @action
    enableQuickFilters = () => {
        this.showQuickFilter = true

        this.quickFilters = this.filters.question.existingIds.union(
            this.filters.openEnded.existingIds,
        )

        this.quickFilterGroups = {
            question: RuleGroup.clone(this.filters.question),
            openEnded: RuleGroup.clone(this.filters.openEnded),
        }
    }

    resetQuickFilters = () => {
        this.quickFilters = new Set()
        Reflect.deleteProperty(this, 'quickFilterGroups')
    }

    applyQuickFilter = () => {
        this.showQuickFilter = false
        this.filters.openEnded = RuleGroup.clone(this.quickFilterGroups.openEnded)
        this.saveFilter('question', this.quickFilterGroups.question)
        this.resetQuickFilters()
    }

    cancelQuickFilters = () => {
        this.showQuickFilter = false
        this.resetQuickFilters()
    }

    filterByOpenEnded = (question, label, surveyDataIds, optionId) => {
        const { questionTypeCode } = question
        const ruleId = `${question.id}${optionId ? `_${optionId}` : ''}_${label}`

        if (this.quickFilters.has(ruleId)) {
            this.quickFilterGroups.openEnded.removeRule(question.id, ruleId)
            this.quickFilters.delete(ruleId)
            return
        }

        const questionTitle = getQuestionTitle({
            content: question.content.shortTitle,
            formContent: R.pathOr(null, ['formContent', 'shortTitle'])(question),
            locale: this.locale,
            defaultLocale: this.locale,
            initialLocale: question.metadata.initialLocale,
        })

        this.quickFilters.add(ruleId)
        this.addFilterRule(this.quickFilterGroups.openEnded, questionTitle, question.id, {
            setYId: ruleId, id: ruleId, label, surveyDataIds,
        }, questionTypeCode)
    }

    filterByOption = (question, optionId, setXId) => {
        const { questionTypeCode } = question
        const isMultiAxis = ['SG', 'TB', 'RT'].includes(questionTypeCode)
        const option = (question.optionSet ?? question.optionSetY).options.find(({ id }) => id === optionId)

        const ruleId = isMultiAxis
            ? `${question.id}_${optionId}_${setXId}`
            : `${question.id}_${optionId}`

        if (this.quickFilters.has(ruleId)) {
            this.quickFilterGroups.question.removeRule(question.id, ruleId)
            this.quickFilters.delete(ruleId)
            return
        }

        let label = getOptionTitle({
            langs: option.langs,
            locale: this.locale,
            defaultLocale: this.locale,
            initialLocale: question.metadata.initialLocale,
        })

        if (isMultiAxis) {
            const option = question.optionSetX.options.find(({ id }) => id === setXId)
            const xLabel = getOptionTitle({
                langs: option.langs,
                locale: this.locale,
                defaultLocale: this.locale,
                initialLocale: question.metadata.initialLocale,
            })
            label = `${label} - ${xLabel}`
        }

        const questionTitle = getQuestionTitle({
            content: question.content.shortTitle,
            formContent: R.pathOr(null, ['formContent', 'shortTitle'])(question),
            locale: this.locale,
            defaultLocale: this.locale,
            initialLocale: question.metadata.initialLocale,
        })

        this.quickFilters.add(ruleId)
        this.addFilterRule(this.quickFilterGroups.question, questionTitle, question.id, {
            setYId: optionId, id: ruleId, label, setXId,
        }, questionTypeCode)
    }

    addFilterRule = (ruleGroup, label, id, rule, questionTypeCode) => {
        const logic = ['DT'].includes(questionTypeCode) ? 'AND' : 'OR'

        ruleGroup.addRule(new Rule(
            {
                id,
                label,
                options: [rule],
                logic,
                questionTypeCode,
            },
        ), questionTypeCode === 'DT')
    }
    /** End quick filtering */

    toggleShowExportModal = () => {
        this.showExportModal = !this.showExportModal
    }

    isAiSummaryEnabled = () => {
        const { appStore } = this

        return R.pathOr(false, ['userData', 'flags', 'isAiSummaryEnabled'], appStore)
            && R.pathOr(false, ['metadata', 'isSurveyAiSummaryEnabled'], appStore.survey)
    }

    /** AI report summary */
    initReportLoadSummary = async () => {
        const { appStore } = this
        if (process.env.SHOW_AI_ASSISTANT
                && !R.pathOr(false, ['metadata', 'isRapidSurvey'], appStore.survey)
                && this.isAiSummaryEnabled()
                && (R.pathOr('', ['metadata', 'taxonomy', 'surveyType'], appStore.survey) !== 'EM')) {
            await this.fetchSummary('reportOpen')
        }
    }

    getSummaryStatus = () => {
        const { summaryLastUpdated, summaryNumberOfResponses, v2ReportStats } = this
        const surveyResponses = R.pathOr(0, ['stats', 'aggregate', 'total', 'complete'], v2ReportStats)
        const { status } = this.surveyStatus

        const isInDevelopment = status === 'Created' && surveyResponses === 0
        const isOpen = status === 'Created' && !isInDevelopment

        let defaultDesc = i18n.t('report.aiSummary.status.notGenerated.hover')
        if (isInDevelopment) {
            defaultDesc = i18n.t('report.aiSummary.status.notGenerated.hover.develop')
        } else if (isOpen) {
            defaultDesc = i18n.t('report.aiSummary.status.notGenerated.hover.open')
        }

        let summaryStatus = {
            label: i18n.t('report.aiSummary.status.notGenerated'),
            style: 'archive',
            icon: '',
            description: defaultDesc,
        }

        if (summaryLastUpdated) {
            if (surveyResponses - summaryNumberOfResponses === 0) {
                summaryStatus = {
                    label: i18n.t('report.aiSummary.status.upToDate'),
                    style: 'success',
                    icon: ' fa-solid fa-circle-info',
                    description: i18n.t(`report.aiSummary.status.upToDate.hover${isOpen ? '.open' : ''}`),
                }
            } else {
                summaryStatus = {
                    label: i18n.t('report.aiSummary.status.outOfDate'),
                    style: 'warning',
                    icon: 'fa-solid fa-circle-info',
                    description: i18n.t(`report.aiSummary.status.outOfDate.hover${isOpen ? '.open' : ''}`),
                }
            }
        }

        return summaryStatus
    }

    fetchSummary = async (source) => {
        const { appStore, surveyId } = this

        if (this.summaryPollCount === 50) {
            this.generatingSummary = false
            appStore.addNotification(null, i18n.t('s_report_ai_generation_time_out'), 'error')
            return
        }

        this.summaryStatus = this.getSummaryStatus()
        try {
            const response = await appStore.http.get(`/report/v1/${surveyId}/summary?triggerSource=${source}`)

            const status = R.pathOr('', ['status'], response)

            if (status === 'manual_refresh_needed') {
                appStore.addNotification(null, i18n.t('s_report_ai_summary_manual_refresh_warning'), 'warning')
            }

            if (['ai_summary_running', 'trigger_new_ai_summary'].includes(status)) {
                this.generatingSummary = true
                this.summaryPollCount += 1
                this.fetchTimeout = setTimeout(() => this.fetchSummary(source), 10000)
                return
            }

            if (response.summary) {
                this.AiSummary = response.summary
                this.summaryLastUpdated = response.lastUpdated
                this.generatingSummary = false
                this.summaryNumberOfResponses = response.numberOfResponses

                this.summaryStatus = this.getSummaryStatus()
                return
            }

            if (status === 'no_existing_summary') {
                this.summaryStatus = this.getSummaryStatus()
                return
            }

            appStore.addNotification(null, `${i18n.t('s_report_ai_chat_error_fetching')}: ${status}`, 'error')
            this.generatingSummary = false
        } catch (e) {
            appStore.addNotification(null, i18n.t('s_report_ai_chat_error_fetching'), 'error')
            console.log(e)
        }
    }

    toggleShowAiAssistant = () => {
        this.showAiAssistant = !this.showAiAssistant
    }

    refreshAiSummary = async () => {
        const { appStore, surveyId } = this
        const { userData: { role } } = appStore
        const isReadOnlyUser = role >= 150
        if (isReadOnlyUser) {
            appStore.addNotification(null, i18n.t('s_report_ai_error_refreshing_summary'), 'error')
            return
        }

        try {
            const res = await appStore.http.get(`/report/v1/${surveyId}/summary/generate?triggerSource=refresh`)
            const notification = {
                message: '',
                type: 'error',
            }

            const cantRefresh = i18n.t('s_report_ai_error_cannot_refresh_prefix')
            const refreshing = i18n.t('s_report_ai_refreshing_summary')
            const upToDate = i18n.t('s_report_ai_summary_up_to_date')

            switch (res.status) {
                case 'no_responses_yet':
                    notification.message = `${cantRefresh}${i18n.t('s_report_ai_summary_no_responses')}`
                    break
                case 'not_enough_responses':
                    notification.message = `${cantRefresh}${i18n.t('s_report_ai_summary_not_enough_responses')}`
                    break
                case 'manual_refresh_limit':
                    notification.message = `${cantRefresh}${i18n.t('s_report_ai_summary_refresh_limit')}`
                    break
                case 'invalid_parameters':
                    notification.message = `${cantRefresh}${i18n.t('s_report_ai_summary_invalid_parameters')}`
                    break
                case 'ai_summary_running':
                    notification.message = `${cantRefresh}${i18n.t('s_report_ai_summary_already_in_progress')}`
                    break
                case 'fetch_existing_summary':
                    notification.message = upToDate
                    notification.type = 'success'
                    break
                case 'trigger_new_ai_summary':
                    notification.message = refreshing
                    notification.type = 'success'
                    this.generatingSummary = true
                    break
                default:
                    notification.message = i18n.t('s_report_ai_error_refreshing_summary')
                    break
            }

            appStore.addNotification(null, notification.message, notification.type)

            if (['ai_summary_running', 'trigger_new_ai_summary'].includes(res.status)) {
                this.fetchTimeout = setTimeout(() => this.fetchSummary('refresh'), 10000)
            }
        } catch (e) {
            appStore.addNotification(null, i18n.t('s_report_ai_error_refreshing_summary'), 'error')
        }
    }

    copyAiSummary = async () => {
        const { appStore } = this
        if (!this.AiSummary) {
            appStore.addNotification(null, i18n.t('s_report_ai_txt_copied_error'), 'error')
            return
        }

        let txt = ''

        Object.entries(this.AiSummary.keyFindings).forEach((kf) => {
            txt += `${kf[0]}\n`
            kf[1].forEach((f) => {
                txt += `- ${f}\n`
            })

            txt += '\n\n'
        })

        try {
            await window.navigator.clipboard.writeText(txt)
            appStore.addNotification(null, i18n.t('s_report_ai_txt_copied'), 'success')
        } catch (e) {
            appStore.addNotification(null, i18n.t('s_report_ai_txt_copied_error'), 'error')
        }
    }
    /** End AI report summary */
}

export default ReportStore
